import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

echarts.use([TooltipComponent, GridComponent, LegendComponent, LineChart, CanvasRenderer]);

interface AbuseDriftChartProps {
  data: { eventIndex: number; abuseScore: number }[];
  title: string;
  description: string;
}

const AbuseDriftChart: React.FC<AbuseDriftChartProps> = ({ data, title, description }) => {
  const { getThemeColor } = useAppContext();

  // Sort data by event index
  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => a.eventIndex - b.eventIndex);
  }, [data]);

  const eventIndices = sortedData.map(item => item.eventIndex);
  const abuseScores = sortedData.map(item => item.abuseScore);

  const maxScore = abuseScores.length > 0 ? Math.max(...abuseScores) : 100;
  const yAxisMax = Math.ceil((maxScore * 1.2) / 10) * 10 || 100;
  const avgScore = abuseScores.length > 0 ? abuseScores.reduce((a, b) => a + b, 0) / abuseScores.length : 0;
  const lineColor = avgScore >= 70 ? '#f44336' : avgScore >= 40 ? '#ff9800' : '#4caf50';

  // Ensure we have valid colors with fallbacks
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const options = {
    color: lineColor,
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        return `<strong>Event ${param.name}:</strong> ${param.value.toFixed(1)}%`;
      }
    },
    xAxis: {
      type: 'category',
      name: 'Event Index',
      data: eventIndices,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor }
    },
    yAxis: {
      type: 'value',
      name: 'Abuse Score (%)',
      min: 0,
      max: yAxisMax,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor }
    },
    series: [
      {
        type: 'line',
        data: abuseScores,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          color: lineColor,
          width: 2
        },
        itemStyle: {
          color: lineColor
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: lineColor + '40'
              },
              {
                offset: 1,
                color: lineColor + '10'
              }
            ]
          }
        }
      }
    ],
    grid: { left: '15%', right: '10%', bottom: '15%', top: '15%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">{title}</h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '400px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default AbuseDriftChart;
